/* ═══════════════════════════════════════════════════════════════
   Address codec — pure. Every screen/record has ONE address.
   Web path = `/projects/:id`; desktop = `#` + the same path (the hash
   router strips the `#`, so this module only ever sees `/path?query`).

   toAddress(DeepLink | view | ParsedAddress) -> '/path?query'
   fromAddress('/path?query')                 -> ParsedAddress

   No React, no window, no I/O (MULTI_WINDOW_PLAN). The shell feeds a
   ParsedAddress into the EXISTING deep-link consumer path.
   ═══════════════════════════════════════════════════════════════ */

import type { DeepLink, TimeExpenseTab } from '../types/navigation';
import { parseTaskUrlLink } from '../pages/tasks/taskUrlLink';

export type ReviewLane = 'master' | 'forma';

export type ParsedAddress =
  /** Plain screens with no deep-link payload (view id as app.tsx knows it). */
  | { kind: 'view'; view: string; tab?: string; v?: string }
  /** Screens whose page consumes a DeepLink one-shot. */
  | { kind: 'link'; link: DeepLink; v?: string }
  | { kind: 'spec-studio'; masterId: string; variantId?: string }
  | { kind: 'project-spec'; projectId: string; specId: string; review?: ReviewLane }
  | { kind: 'project-forma'; projectId: string; formaId: string }
  | { kind: 'forma-studio'; formaId: string }
  | { kind: 'module'; moduleId: string; v?: string }
  | { kind: 'client'; clientId: string; v?: string }
  /** Legacy `/?view=tasks&taskId=X` — the client replaces it with `to`. */
  | { kind: 'redirect'; to: string }
  | { kind: 'unknown' };

/** App view id -> first path segment (plain list screens). */
const VIEW_PATH: Record<string, string> = {
  home: '',
  projects: 'projects',
  'spec-masters': 'specs',
  modules: 'modules',
  formas: 'formas',
  format: 'formatter',
  tasks: 'tasks',
  'time-expense': 'time',
  invoices: 'billing',
  forecasting: 'forecasting',
  reports: 'reporting',
  clients: 'clients',
  team: 'people',
  admin: 'admin',
  settings: 'settings',
  // Not in the plan table: the "Add Specs" screen (view id `compile`).
  compile: 'add-specs',
};

const PATH_VIEW: Record<string, string> = Object.fromEntries(
  Object.entries(VIEW_PATH)
    .filter(([, p]) => p !== '')
    .map(([view, p]) => [p, view])
);

const TIME_TABS: readonly TimeExpenseTab[] = ['timesheets', 'forecasting', 'expenses', 'vacation'];
const PROJECT_TABS = ['dashboard', 'specs', 'formas'] as const;

const ID_MAX = 200;

function seg(id: string): string {
  return encodeURIComponent(id);
}

function query(pairs: Array<[string, string | undefined]>): string {
  const p = new URLSearchParams();
  for (const [k, v] of pairs) if (v) p.set(k, v);
  const s = p.toString();
  return s ? `?${s}` : '';
}

function viewPath(view: string): string {
  const p = VIEW_PATH[view];
  return p ? `/${p}` : '/';
}

function linkToAddress(link: DeepLink, v?: string): string {
  switch (link.view) {
    case 'time-expense':
      return `/time${query([
        ['tab', link.tab],
        ['projectId', link.projectId],
        ['weekStart', link.weekStart],
        ['memberId', link.memberId],
        ['v', v],
      ])}`;
    case 'forecasting':
      return `/forecasting${query([
        ['projectId', link.projectId],
        ['weekStart', link.weekStart],
        ['memberId', link.memberId],
        ['v', v],
      ])}`;
    case 'invoices': {
      const q = query([
        ['projectId', link.projectId],
        ['status', link.status],
        ['v', v],
      ]);
      if (link.create) return `/billing/new${q}`;
      if (link.invoiceId) return `/billing/${seg(link.invoiceId)}${q}`;
      return `/billing${q}`;
    }
    case 'team':
      return `${link.memberId ? `/people/${seg(link.memberId)}` : '/people'}${query([['v', v]])}`;
    case 'projects':
      return `${link.projectId ? `/projects/${seg(link.projectId)}` : '/projects'}${query([
        ['tab', link.tab],
        ['v', v],
      ])}`;
    case 'compile':
      return `/add-specs${query([['projectId', link.projectId]])}`;
    case 'tasks': {
      const q = query([
        ['projectId', link.projectId],
        ['v', v],
      ]);
      if (link.create) return `/tasks/new${q}`;
      if (link.taskId) return `/tasks/${seg(link.taskId)}${q}`;
      return `/tasks${q}`;
    }
  }
}

export type AddressTarget = DeepLink | ParsedAddress | string;

/** DeepLink | view id | ParsedAddress -> address. Unknown view id -> '/'. */
export function toAddress(target: AddressTarget): string {
  if (typeof target === 'string') return viewPath(target);
  if (!('kind' in target)) return linkToAddress(target);
  switch (target.kind) {
    case 'view':
      return `${viewPath(target.view)}${query([
        ['tab', target.tab],
        ['v', target.v],
      ])}`;
    case 'link':
      return linkToAddress(target.link, target.v);
    case 'spec-studio':
      return `/specs/${seg(target.masterId)}${query([['variant', target.variantId]])}`;
    case 'project-spec':
      return `/projects/${seg(target.projectId)}/specs/${seg(target.specId)}${query([
        ['review', target.review],
      ])}`;
    case 'project-forma':
      return `/projects/${seg(target.projectId)}/formas/${seg(target.formaId)}`;
    case 'forma-studio':
      return `/formas/${seg(target.formaId)}`;
    case 'module':
      return `/modules/${seg(target.moduleId)}${query([['v', target.v]])}`;
    case 'client':
      return `/clients/${seg(target.clientId)}${query([['v', target.v]])}`;
    case 'redirect':
      return target.to;
    case 'unknown':
      return '/';
  }
}

/** Drop undefined keys so parsed objects compare cleanly. */
function clean<T extends object>(o: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, val] of Object.entries(o)) if (val !== undefined) out[k] = val;
  return out as T;
}

function safeId(raw: string | undefined): string | null {
  if (!raw) return null;
  let id: string;
  try {
    id = decodeURIComponent(raw);
  } catch {
    return null;
  }
  return id.length > 0 && id.length <= ID_MAX ? id : null;
}

/** Address ('/path?query', hash already stripped) -> ParsedAddress. */
export function fromAddress(address: string): ParsedAddress {
  let url: URL;
  try {
    url = new URL(address, 'http://specforma.local');
  } catch {
    return { kind: 'unknown' };
  }
  const q = url.searchParams;
  const get = (k: string): string | undefined => {
    const x = q.get(k);
    return x ? x.slice(0, ID_MAX) : undefined;
  };
  const segs = url.pathname.split('/').filter((s) => s.length > 0);

  // Home — and the legacy task-email link, kept forever.
  if (segs.length === 0) {
    const legacy = parseTaskUrlLink(url.search);
    if (legacy) return { kind: 'redirect', to: `/tasks/${seg(legacy.taskId)}` };
    return { kind: 'view', view: 'home' };
  }

  // Unknown first segment (incl. reserved /portfolio and the proxied
  // /api /auth /ws /collab) is the unknown-address rule.
  const view = PATH_VIEW[segs[0]];
  if (!view) return { kind: 'unknown' };
  const v = get('v');
  const id = segs.length >= 2 ? safeId(segs[1]) : null;
  if (segs.length >= 2 && !id) return { kind: 'unknown' };
  const vPart: { v?: string } = v ? { v } : {};

  switch (view) {
    case 'projects': {
      if (segs.length === 1) return { kind: 'link', link: { view: 'projects' }, ...vPart };
      const projectId = id as string;
      if (segs.length === 2) {
        const tab = PROJECT_TABS.find((x) => x === q.get('tab'));
        return {
          kind: 'link',
          link: clean({ view: 'projects' as const, projectId, tab }),
          ...vPart,
        };
      }
      const rec = segs.length === 4 ? safeId(segs[3]) : null;
      if (rec && segs[2] === 'specs') {
        const r = q.get('review');
        const review: ReviewLane | undefined = r === 'master' || r === 'forma' ? r : undefined;
        return clean({ kind: 'project-spec' as const, projectId, specId: rec, review });
      }
      if (rec && segs[2] === 'formas') {
        return { kind: 'project-forma', projectId, formaId: rec };
      }
      return { kind: 'unknown' };
    }
    case 'spec-masters':
      if (segs.length === 1) return { kind: 'view', view, ...vPart };
      if (segs.length === 2)
        return clean({
          kind: 'spec-studio' as const,
          masterId: id as string,
          variantId: get('variant'),
        });
      return { kind: 'unknown' };
    case 'formas':
      if (segs.length === 1) return { kind: 'view', view };
      if (segs.length === 2) return { kind: 'forma-studio', formaId: id as string };
      return { kind: 'unknown' };
    case 'modules':
      if (segs.length === 1) return { kind: 'view', view, ...vPart };
      if (segs.length === 2) return { kind: 'module', moduleId: id as string, ...vPart };
      return { kind: 'unknown' };
    case 'clients':
      if (segs.length === 1) return { kind: 'view', view, ...vPart };
      if (segs.length === 2) return { kind: 'client', clientId: id as string, ...vPart };
      return { kind: 'unknown' };
    case 'admin':
    case 'settings':
      if (segs.length !== 1) return { kind: 'unknown' };
      return clean({ kind: 'view' as const, view, tab: get('tab') });
    case 'format':
    case 'reports':
      return segs.length === 1 ? { kind: 'view', view } : { kind: 'unknown' };
    case 'time-expense': {
      if (segs.length !== 1) return { kind: 'unknown' };
      const tab = TIME_TABS.find((x) => x === q.get('tab'));
      return {
        kind: 'link',
        link: clean({
          view: 'time-expense' as const,
          tab,
          projectId: get('projectId'),
          weekStart: get('weekStart'),
          memberId: get('memberId'),
        }),
        ...vPart,
      };
    }
    case 'forecasting':
      if (segs.length !== 1) return { kind: 'unknown' };
      return {
        kind: 'link',
        link: clean({
          view: 'forecasting' as const,
          projectId: get('projectId'),
          weekStart: get('weekStart'),
          memberId: get('memberId'),
        }),
        ...vPart,
      };
    case 'invoices': {
      if (segs.length > 2) return { kind: 'unknown' };
      const st = q.get('status');
      const status: 'overdue' | 'all' | undefined =
        st === 'overdue' || st === 'all' ? st : undefined;
      const base = { view: 'invoices' as const, projectId: get('projectId'), status };
      if (id === 'new') return { kind: 'link', link: clean({ ...base, create: true }), ...vPart };
      return {
        kind: 'link',
        link: clean({ ...base, invoiceId: id ?? undefined }),
        ...vPart,
      };
    }
    case 'team':
      if (segs.length > 2) return { kind: 'unknown' };
      return {
        kind: 'link',
        link: clean({ view: 'team' as const, memberId: id ?? undefined }),
        ...vPart,
      };
    case 'tasks': {
      if (segs.length > 2) return { kind: 'unknown' };
      const base = { view: 'tasks' as const, projectId: get('projectId') };
      if (id === 'new') return { kind: 'link', link: clean({ ...base, create: true }), ...vPart };
      return {
        kind: 'link',
        link: clean({ ...base, taskId: id ?? undefined }),
        ...vPart,
      };
    }
    case 'compile':
      if (segs.length !== 1) return { kind: 'unknown' };
      return {
        kind: 'link',
        link: clean({ view: 'compile' as const, projectId: get('projectId') }),
      };
    default:
      return { kind: 'unknown' };
  }
}

/* ── Surface keys: "is the screen already showing this?" ─────────────
   Coarse on purpose (ignores filters/ids that pages own) so the shell
   never pushes an address for a state it is already in. */

export interface ShellSurfaceState {
  view: string;
  studioMasterId: string | null;
  editorProjectSpecId: string | null;
  studioFormaId: string | null;
}

export function surfaceOfParsed(p: ParsedAddress): string {
  switch (p.kind) {
    case 'view':
      return p.view;
    case 'link':
      return p.link.view;
    case 'spec-studio':
      return `spec-studio:${p.masterId}`;
    case 'project-spec':
      return `projects:spec:${p.specId}`;
    case 'project-forma':
      return `projects:forma:${p.formaId}`;
    case 'forma-studio':
      return `forma-studio:${p.formaId}`;
    case 'module':
      return 'modules';
    case 'client':
      return 'clients';
    case 'redirect':
    case 'unknown':
      return 'unknown';
  }
}

export function surfaceOfState(s: ShellSurfaceState): string {
  if (s.view === 'spec-studio' && s.studioMasterId) return `spec-studio:${s.studioMasterId}`;
  if (s.view === 'projects' && s.editorProjectSpecId)
    return `projects:spec:${s.editorProjectSpecId}`;
  if (s.view === 'forma-studio')
    return s.studioFormaId ? `forma-studio:${s.studioFormaId}` : 'formas';
  return s.view;
}

/** Address for the shell's current state; null when it cannot be formed. */
export function addressOfState(
  s: ShellSurfaceState,
  sourceProjectId: string | null
): string | null {
  if (s.view === 'spec-studio' && s.studioMasterId)
    return toAddress({ kind: 'spec-studio', masterId: s.studioMasterId });
  if (s.view === 'projects' && s.editorProjectSpecId) {
    return sourceProjectId
      ? toAddress({
          kind: 'project-spec',
          projectId: sourceProjectId,
          specId: s.editorProjectSpecId,
        })
      : null;
  }
  if (s.view === 'forma-studio') {
    return s.studioFormaId
      ? toAddress({ kind: 'forma-studio', formaId: s.studioFormaId })
      : '/formas';
  }
  if (s.view === 'spec-studio') return '/specs';
  if (!(s.view in VIEW_PATH)) return null;
  return viewPath(s.view);
}

/* ── Titles ─────────────────────────────────────────────────────── */

export const VIEW_LABELS: Record<string, string> = {
  home: 'Home',
  clients: 'Clients',
  projects: 'Projects',
  team: 'People',
  tasks: 'Tasks',
  'time-expense': 'Time & Expense',
  invoices: 'Billing',
  forecasting: 'Forecasting',
  reports: 'Reporting',
  admin: 'Admin Center',
  'spec-masters': 'Master Specs',
  modules: 'Modules',
  formas: 'Master Formas',
  format: 'Formatter',
  compile: 'Add Specs',
  settings: 'Settings',
};

export interface TitleNames {
  /** "25025-00" style label for the project in the address, when loaded. */
  project?: string;
  /** Loaded name of the open record (spec / master / forma), when known. */
  record?: string;
}

/** "<record> · <parent> · SpecForma"; "SpecForma" alone on Home. Pure. */
export function titleFor(p: ParsedAddress, names: TitleNames = {}): string {
  const tail = 'SpecForma';
  const parts: string[] = [];
  switch (p.kind) {
    case 'view':
      if (p.view === 'home') return tail;
      parts.push(VIEW_LABELS[p.view] ?? '');
      break;
    case 'link':
      if (p.link.view === 'projects' && p.link.projectId && names.project)
        parts.push(names.project);
      else {
        // A record open inside a list page (task, invoice, person): its name leads.
        const l = p.link;
        const open =
          ('taskId' in l && l.taskId) ||
          ('invoiceId' in l && l.invoiceId) ||
          ('memberId' in l && l.view === 'team' && l.memberId);
        if (open && names.record) parts.push(names.record);
        parts.push(VIEW_LABELS[p.link.view] ?? '');
      }
      break;
    case 'project-spec':
    case 'project-forma':
      if (names.record) parts.push(names.record);
      parts.push(names.project ?? 'Projects');
      break;
    case 'spec-studio':
      if (names.record) parts.push(names.record);
      parts.push('Master Specs');
      break;
    case 'forma-studio':
      if (names.record) parts.push(names.record);
      parts.push('Master Formas');
      break;
    case 'module':
      if (names.record) parts.push(names.record);
      parts.push('Modules');
      break;
    case 'client':
      if (names.record) parts.push(names.record);
      parts.push('Clients');
      break;
    case 'redirect':
    case 'unknown':
      return tail;
  }
  return [...parts.filter(Boolean), tail].join(' · ');
}
